import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Sun, Moon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { KeyIndicatorsBar } from './MarketDataCards';
import { useLayoutMaster } from '../utils/useLayoutMaster';
import { useAuth } from '../context/AuthContext';
import { ReactComponent as LogoMagma } from '../assets/logos/LOGO RR MAGMA.svg';

const SITE_NAV = [
  { label: 'Mercado Overview',         href: '/mercado',    icon: '◆' },
  { label: 'Teses de Investimento',    href: '/thesis',     icon: '◈' },
  { label: 'B3 · Ações',              href: '/stocks',     icon: '◇' },
  { label: 'Cripto',                   href: '/crypto',     icon: '₿' },
  { label: 'Câmbio & FX',             href: '/fx',         icon: '◆' },
  { label: 'Renda Fixa & Bonds',       href: '/bonds',      icon: '◇' },
  { label: 'Índices Globais',          href: '/indices',    icon: '◆' },
  { label: 'Commodities',             href: '/commodities',icon: '◇' },
  { label: 'Genoma · SISCOMEX',        href: '/genoma',     icon: '⬡' },
  { label: 'Calendário Econômico',     href: '/calendario', icon: '◈' },
  { label: 'RR TV',                    href: '/rrtv',       icon: '▶', badge: 'AO VIVO' },
];

const SECTION_NAV = [
  { label: 'Cenários / Teses',              href: '/thesis'      },
  { label: 'Genoma',                        href: '/genoma'      },
  { label: 'Comportamento de Mercado',      href: '/fx'          },
  { label: 'Insights Não Convencionais',    href: '/bonds'       },
  { label: 'RR TV',                         href: '/rrtv',  badge: 'AO VIVO' },
  {
    label: 'Mercado',
    dropdown: [
      { label: 'B3 · Ações',    href: '/stocks'      },
      { label: 'Câmbio',        href: '/fx'          },
      { label: 'Cripto',        href: '/crypto'      },
      { label: 'Índices',       href: '/indices'     },
      { label: 'Commodities',   href: '/commodities' },
      { label: 'Renda Fixa',    href: '/bonds'       },
    ],
  },
];

const TRENDING = [
  { ticker: 'PETR4',   name: 'Petrobras PN',     type: 'stock',    category: '🔥' },
  { ticker: 'VALE3',   name: 'Vale ON',           type: 'stock',    category: '🔥' },
  { ticker: 'ITUB4',   name: 'Itaú Unibanco PN',  type: 'stock',   category: '🔥' },
  { ticker: 'BBDC4',   name: 'Bradesco PN',       type: 'stock',    category: '⭐' },
  { ticker: 'ABEV3',   name: 'Ambev ON',          type: 'stock',    category: '⭐' },
  { ticker: '^BVSP',   name: 'Ibovespa',          type: 'index',    category: '📊' },
  { ticker: 'USDBRL',  name: 'Dólar',             type: 'currency', category: '💱' },
  { ticker: 'BTC-USD', name: 'Bitcoin',           type: 'crypto',   category: '₿' },
];

const DINO_API = '/api/tickers61';

function NavSearch({ marketData }) {
  const navigate = useNavigate();
  const [query, setQuery]     = useState('');
  const [open, setOpen]       = useState(false);
  const [pos, setPos]         = useState({ top: 0, left: 0, width: 0 });
  const [results, setResults] = useState(TRENDING);
  const [sel, setSel]         = useState(0);
  const wrapRef   = useRef(null);
  const inputRef  = useRef(null);
  const portalRef = useRef(null);
  const assets    = useRef(TRENDING);

  // PATCH 1: merge B3 (from marketData prop) + worldwide (from DINO :6061/api/tickers)
  useEffect(() => {
    const b3 = (marketData?.allTickers || []).map(s => ({
      ticker: s.ticker, name: s.name || s.ticker,
      type: 'stock', category: '📈 B3', price: s.ult, change: s.var,
    }));

    fetch(`${DINO_API}/api/tickers`)
      .then(r => r.json())
      .then(world => {
        const seen = new Set(b3.map(x => x.ticker));
        const worldMapped = world
          .filter(t => !seen.has(t.ticker))
          .map(t => ({
            ticker: t.ticker, name: t.name || t.ticker,
            type: 'stock',
            category: `🌍 ${t.exchange || 'Mundial'}`,
            price: t.ult, change: t.var,
            currency: t.currency,
          }));
        assets.current = [...b3, ...worldMapped];
      })
      .catch(() => { assets.current = b3; });
  }, [marketData]);

  useEffect(() => {
    const h = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target) &&
          portalRef.current && !portalRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    if (!query.trim()) { setResults(TRENDING); setSel(0); return; }
    const q = query.toLowerCase();
    const r = assets.current
      .filter(a => a.ticker.toLowerCase().includes(q) || a.name.toLowerCase().includes(q))
      .sort((a, b) => {
        const ae = a.ticker.toLowerCase() === q;
        const be = b.ticker.toLowerCase() === q;
        return ae === be ? 0 : ae ? -1 : 1;
      })
      .slice(0, 8);
    setResults(r); setSel(0);
  }, [query]);

  const openDropdown = () => {
    if (wrapRef.current) {
      const r = wrapRef.current.getBoundingClientRect();
      const isMobileView = window.innerWidth < 768;
      if (isMobileView) {
        setPos({ top: r.bottom + 4, left: 8, width: window.innerWidth - 16 });
      } else {
        setPos({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 300) });
      }
    }
    setOpen(true);
  };

  const handleKey = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel(p => Math.min(p + 1, results.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setSel(p => Math.max(p - 1, 0)); }
    if (e.key === 'Enter')     { if (results[sel]) go(results[sel]); }
    if (e.key === 'Escape')    { setOpen(false); }
  };

  const go = (item) => {
    navigate(`/asset/${item.type}/${item.ticker}`);
    setOpen(false); setQuery('');
  };

  // PATCH 2: currency-aware price display — shows JPY/USD/EUR etc, not R$ for everything
  const fmtPrice = (r) => {
    if (!r.price || r.price <= 0) return null;
    const ccy = r.currency;
    if (!ccy || ccy === 'BRL') return `R$${r.price.toFixed(2)}`;
    // high-unit currencies: no decimals
    const noDecimals = ['JPY', 'KRW', 'IDR', 'CLP', 'TWD'].includes(ccy);
    return `${ccy} ${noDecimals ? r.price.toFixed(0) : r.price.toFixed(2)}`;
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.05)', border: `1px solid ${open ? 'rgba(102,96,254,0.4)' : 'rgba(255,255,255,0.1)'}`, borderRadius: 6, padding: '4px 10px', transition: 'border-color 0.15s' }}>
        <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11, flexShrink: 0 }}>🔍</span>
        <input ref={inputRef} value={query}
          onChange={e => { setQuery(e.target.value); openDropdown(); }}
          onFocus={openDropdown} onKeyDown={handleKey}
          placeholder="Buscar ativo..."
          style={{ flex: 1, minWidth: 0, background: 'transparent', border: 'none', outline: 'none', color: '#fff', fontSize: 11, fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontWeight: 400 }}
        />
        {query && (
          <button onClick={() => { setQuery(''); inputRef.current?.focus(); }}
            style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', cursor: 'pointer', fontSize: 10, padding: 0, flexShrink: 0 }}>✕</button>
        )}
      </div>

      {open && ReactDOM.createPortal(
        <div ref={portalRef} style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, background: '#08060f', border: '1px solid rgba(102,96,254,0.2)', borderRadius: 8, overflow: 'hidden', boxShadow: '0 12px 40px rgba(0,0,0,0.7)', zIndex: 9999 }}>
          <div style={{ padding: '8px 12px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'rgba(255,255,255,0.3)' }}>
              {query ? `${results.length} RESULTADO(S)` : '🔥 TRENDING'}
            </span>
            <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 9, color: 'rgba(255,255,255,0.2)' }}>↑↓ • Enter</span>
          </div>
          <div style={{ maxHeight: 320, overflowY: 'auto' }}>
            {results.length === 0
              ? <div style={{ padding: '16px 12px', color: 'rgba(255,255,255,0.3)', fontSize: 11, fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontWeight: 400 }}>Nenhum resultado para "{query}"</div>
              : results.map((r, i) => (
                <div key={r.ticker} onClick={() => go(r)}
                  style={{ padding: '9px 12px', cursor: 'pointer', background: i === sel ? 'rgba(102,96,254,0.1)' : 'transparent', borderBottom: '1px solid rgba(255,255,255,0.04)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'background 0.1s' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(102,96,254,0.08)'; setSel(i); }}
                  onMouseLeave={e => { e.currentTarget.style.background = i === sel ? 'rgba(102,96,254,0.1)' : 'transparent'; }}
                >
                  <div>
                    <span style={{ fontFamily: "'Roble',system-ui,sans-serif", fontSize: 12, fontWeight: 800, color: '#D4AF37' }}>{r.ticker}</span>
                    <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', marginLeft: 6 }}>{r.category}</span>
                    <div style={{ fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontWeight: 400, fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 2 }}>{r.name}</div>
                  </div>
                  {fmtPrice(r) && (
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontSize: 11, fontWeight: 600, color: '#fff' }}>{fmtPrice(r)}</div>
                      {r.change !== undefined && (
                        <div style={{ fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontSize: 10, fontWeight: 600, color: r.change >= 0 ? '#4ade80' : '#f87171' }}>
                          {r.change >= 0 ? '▲' : '▼'}{Math.abs(r.change).toFixed(2)}%
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            }
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function SiteDrawer({ open, onClose }) {
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  return ReactDOM.createPortal(
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(5px)', opacity: open ? 1 : 0, pointerEvents: open ? 'all' : 'none', transition: 'opacity 0.26s ease' }} />
      <div style={{ position: 'fixed', top: 0, left: 0, bottom: 0, width: 320, zIndex: 9999, background: '#060410', borderRight: '1px solid rgba(102,96,254,0.18)', transform: open ? 'translateX(0)' : 'translateX(-320px)', transition: 'transform 0.32s cubic-bezier(0.16,1,0.3,1)', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
        <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 7, fontWeight: 600, letterSpacing: '0.28em', color: 'rgba(255,255,255,0.2)', marginBottom: 8 }}>RELATÓRIO RESERVADO</div>
            <LogoMagma style={{ height: 24, width: 'auto', display: 'block' }} />
          </div>
          <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 4, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.45)', fontSize: 13, cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ padding: '8px 0 8px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ fontFamily: "'IBM Plex Mono',monospace", padding: '4px 20px 8px', fontSize: 8, fontWeight: 700, letterSpacing: '0.2em', color: 'rgba(255,255,255,0.22)' }}>PÁGINAS</div>
          {SITE_NAV.map((item) => (
            <a key={item.href} href={item.href} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 20px', textDecoration: 'none', borderBottom: '1px solid rgba(255,255,255,0.04)', transition: 'background 0.1s' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <span style={{ fontSize: 11, color: 'rgba(102,96,254,0.7)', width: 14 }}>{item.icon}</span>
              <span style={{ fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.75)', flex: 1 }}>{item.label}</span>
              {item.badge && <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 7, fontWeight: 800, letterSpacing: '0.12em', padding: '2px 5px', borderRadius: 2, background: '#6660fe', color: '#fff' }}>● {item.badge}</span>}
            </a>
          ))}
        </div>
        <div style={{ padding: '14px 20px', marginTop: 'auto', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <p style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 9, color: 'rgba(255,255,255,0.18)', letterSpacing: '0.1em', margin: 0 }}>© 2025 RELATÓRIO RESERVADO</p>
        </div>
      </div>
    </>,
    document.body
  );
}

function NavDropdown({ item }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos]   = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const ref    = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleOpen = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left });
    }
    setOpen(o => !o);
  };

  return (
    <div ref={ref}>
      <button ref={btnRef} onClick={handleOpen} style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: 3, fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontSize: 12, fontWeight: 600, color: open ? '#fff' : 'rgba(255,255,255,0.5)', transition: 'color 0.12s' }}>
        {item.label} <span style={{ fontSize: 8, opacity: 0.6, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.18s', display: 'inline-block' }}>▾</span>
      </button>
      {open && ReactDOM.createPortal(
        <div style={{ position: 'fixed', top: pos.top, left: pos.left, background: '#08060f', border: '1px solid rgba(102,96,254,0.2)', borderRadius: 6, padding: '4px 0', minWidth: 180, boxShadow: '0 8px 32px rgba(0,0,0,0.6)', zIndex: 9999 }}>
          {item.dropdown.map((sub) => (
            <a key={sub.href} href={sub.href} style={{ display: 'block', padding: '9px 16px', textDecoration: 'none', fontSize: 11, fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontWeight: 600, color: 'rgba(255,255,255,0.55)', transition: 'all 0.1s' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(102,96,254,0.08)'; e.currentTarget.style.color = 'rgba(255,255,255,0.9)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.55)'; }}
            >{sub.label}</a>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

function AuthAvatar() {
  const { user, isLoggedIn, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [pos, setPos]   = useState({ top: 0, right: 0 });
  const ref             = useRef(null);

  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setTimeout(() => setOpen(false), 100);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const handleOpen = () => {
    if (!open && ref.current) {
      const r = ref.current.getBoundingClientRect();
      setPos({ top: r.bottom + 6, right: window.innerWidth - r.right });
    }
    setOpen(o => !o);
  };

  if (!isLoggedIn) {
    return (
      <div onClick={() => { window.location.href = '/entrar'; }}
        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 6, border: '1px solid rgba(102,96,254,0.3)', cursor: 'pointer', fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.55)', letterSpacing: '0.1em', transition: 'all 0.15s' }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(102,96,254,0.7)'; e.currentTarget.style.color = '#fff'; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(102,96,254,0.3)'; e.currentTarget.style.color = 'rgba(255,255,255,0.55)'; }}
      >ENTRAR</div>
    );
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={handleOpen} style={{ width: 38, height: 38, borderRadius: '50%', background: 'linear-gradient(135deg,#3d3a99,#6660fe)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 13, border: '2px solid #D4AF37', flexShrink: 0, fontFamily: "'Roble',system-ui,sans-serif", cursor: 'pointer' }}>
        {user.name?.[0]?.toUpperCase() || 'U'}
      </button>
      {open && ReactDOM.createPortal(
        <div style={{ position: 'fixed', top: pos.top, right: pos.right, background: '#08060f', border: '1px solid rgba(102,96,254,0.2)', borderRadius: 8, padding: '8px 0', minWidth: 200, boxShadow: '0 8px 32px rgba(0,0,0,0.7)', zIndex: 9999 }}>
          <div style={{ padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ fontFamily: "'Roble',system-ui,sans-serif", fontSize: 12, fontWeight: 800, color: '#fff', marginBottom: 2 }}>{user.name}</div>
            <div style={{ fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontWeight: 400, fontSize: 9, color: 'rgba(255,255,255,0.35)' }}>{user.email}</div>
            <div style={{ fontFamily: "'IBM Plex Mono',monospace", marginTop: 6, display: 'inline-block', fontSize: 8, fontWeight: 700, letterSpacing: '0.12em', padding: '2px 8px', borderRadius: 3, background: user.tier >= 3 ? '#22c55e22' : user.tier >= 2 ? '#D4AF3722' : '#6660fe22', color: user.tier >= 3 ? '#22c55e' : user.tier >= 2 ? '#D4AF37' : '#6660fe' }}>
              {user.tier >= 3 ? 'FULL MAGMA' : user.tier >= 2 ? 'TIER 1' : 'LOGADO G'}
            </div>
          </div>
          <div onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(false); window.location.href = '/entrar'; }}
            style={{ display: 'block', padding: '10px 16px', fontSize: 11, fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontWeight: 400, color: 'rgba(255,255,255,0.6)', cursor: 'pointer' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >Minha conta</div>
          <div onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); logout(); setOpen(false); window.location.href = '/'; }}
            style={{ display: 'block', padding: '10px 16px', fontSize: 11, fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontWeight: 400, color: 'rgba(220,20,60,0.7)', cursor: 'pointer' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(220,20,60,0.06)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >Sair</div>
        </div>,
        document.body
      )}
    </div>
  );
}

export function Header({ theme, isDark, toggleTheme, topTickers, marketData, currency = 'BRL' }) {
  const layout = useLayoutMaster();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sticky, setSticky]         = useState(false);
  const currencySymbol = currency === 'USD' ? '$' : 'R$';

  useEffect(() => {
    const onScroll = () => setSticky(window.scrollY > 300);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <SiteDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      {sticky && <div style={{ height: 90 }} />}
      <style>{`@keyframes rrSlideDown{from{transform:translateY(-100%);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>
      <header style={{ background: theme.background, backdropFilter: 'blur(10px)', borderBottom: `1px solid ${theme.border}`, position: sticky ? 'fixed' : 'relative', top: 0, left: 0, right: 0, zIndex: 500, padding: layout.isMobile ? '0.5rem 0' : '0.75rem 0 0', animation: sticky ? 'rrSlideDown 0.2s ease' : 'none' }}>
        <div style={layout.container(layout.isMobile ? '0 1rem' : '0 2rem')}>

          {/* ── Top row: hamburger · logo · [KeyIndicators desktop] · theme · avatar ── */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', paddingBottom: layout.isMobile ? 0 : '0.6rem' }}>
            {/* Left: hamburger + logo */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0 }}>
              <button onClick={() => setDrawerOpen(true)} style={{ width: 36, height: 36, borderRadius: 6, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, flexShrink: 0 }}>
                {[0, 1, 2].map(i => (
                  <span key={i} style={{ display: 'block', width: i === 1 ? 11 : 15, height: 1.5, background: 'rgba(255,255,255,0.6)', borderRadius: 1 }} />
                ))}
              </button>
              <a href="/mercado" style={{ display: 'flex', flexDirection: 'column', gap: 4, textDecoration: 'none', cursor: 'pointer' }}>
                {!layout.isMobile && <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 7, fontWeight: 600, letterSpacing: '0.28em', color: 'rgba(255,255,255,0.2)' }}>RELATÓRIO RESERVADO</span>}
                <LogoMagma style={{ height: layout.isMobile ? 20 : 26, width: 'auto', display: 'block' }} />
              </a>
            </div>

            {/* Center: KeyIndicatorsBar — desktop only, fills space */}
            {!layout.isMobile && (
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <KeyIndicatorsBar theme={theme} />
              </div>
            )}

            {/* Right: theme toggle + avatar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0 }}>
              <button onClick={toggleTheme} style={{ padding: '0.5rem', borderRadius: '50%', background: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(102,96,254,0.1)', border: 'none', cursor: 'pointer', color: '#D4AF37', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.3s' }}>
                {isDark ? <Sun size={20} /> : <Moon size={20} />}
              </button>
              <AuthAvatar />
            </div>
          </div>

          {/* ── Mobile search row ── */}
          {layout.isMobile && (
            <div style={{ padding: '0.5rem 0 0.4rem' }}>
              <NavSearch marketData={marketData} />
            </div>
          )}

          {/* ── Desktop nav + search row ── */}
          {!layout.isMobile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 2, borderTop: `1px solid ${theme.border}`, paddingTop: '0.3rem', paddingBottom: '0.3rem', overflow: 'visible' }}>
              {SECTION_NAV.map((item) =>
                item.dropdown ? (
                  <NavDropdown key={item.label} item={item} />
                ) : (
                  <a key={item.label} href={item.href} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 8px', borderRadius: 3, textDecoration: 'none', fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.5)', whiteSpace: 'nowrap', transition: 'all 0.12s' }}
                    onMouseEnter={e => { e.currentTarget.style.color = '#fff'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                    onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; e.currentTarget.style.background = 'transparent'; }}
                  >
                    {item.label}
                    {item.badge && <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 7, fontWeight: 800, letterSpacing: '0.1em', padding: '1px 4px', borderRadius: 2, background: '#6660fe', color: '#fff' }}>● {item.badge}</span>}
                  </a>
                )
              )}
              <div style={{ marginLeft: 'auto', width: 260, flexShrink: 0 }}>
                <NavSearch marketData={marketData} />
              </div>
            </div>
          )}
        </div>

        <div style={{ background: theme.tickerBg, borderTop: `1px solid ${theme.border}`, marginTop: sticky ? 0 : '0.25rem', padding: sticky ? '0.15rem 0' : '0.5rem 0', overflow: 'hidden', transition: 'padding 0.2s' }}>
          <div style={{ display: 'flex', gap: sticky ? '0.5rem' : '1.5rem', animation: 'scroll 30s linear infinite', whiteSpace: 'nowrap', padding: '0 1rem', alignItems: 'center' }}>
            {topTickers.map((ticker, i) => {
              const isPositive = ticker.var >= 0;
              const isKey = ['USDBRL', 'GOLD', 'BRENT'].includes(ticker.ticker);
              return sticky ? (
                <div key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 8px', background: isKey ? 'rgba(212,175,55,0.05)' : 'rgba(255,255,255,0.03)', borderRadius: 5, border: `1px solid ${isKey ? 'rgba(212,175,55,0.2)' : 'rgba(255,255,255,0.08)'}` }}>
                  <span style={{ color: 'rgba(255,255,255,0.45)', fontFamily: "'Roble',system-ui,sans-serif", fontWeight: 800, fontSize: '0.68rem' }}>{ticker.ticker}</span>
                  <span style={{ color: theme.textPrimary, fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontSize: '0.72rem', fontWeight: 600 }}>{currencySymbol}{ticker.ult.toFixed(2)}</span>
                  <span style={{ color: isPositive ? '#4ade80' : '#f87171', fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontWeight: 600, fontSize: '0.68rem' }}>{isPositive ? '▲' : '▼'}{Math.abs(ticker.var).toFixed(2)}%</span>
                </div>
              ) : (
                <div key={i} style={{ display: 'inline-flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', gap: '0.25rem', padding: '0.5rem 1rem', background: isKey ? 'rgba(212,175,55,0.05)' : 'rgba(255,255,255,0.03)', borderRadius: 12, border: `1px solid ${isKey ? 'rgba(212,175,55,0.3)' : 'rgba(255,255,255,0.1)'}`, transition: 'all 0.3s', cursor: 'pointer', minWidth: 120 }}
                  onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                  onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  <span style={{ backgroundColor: isPositive ? '#16a34a' : '#dc2626', color: '#fff', padding: '2px 8px', borderRadius: 6, fontFamily: "'Roble',system-ui,sans-serif", fontWeight: 800, fontSize: '0.75rem', letterSpacing: '0.5px' }}>{ticker.name}</span>
                  <div style={{ fontFamily: "'Roble',system-ui,sans-serif", fontWeight: 800 }}>{ticker.ticker}</div>
                  <span style={{ color: theme.textPrimary, fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontSize: '0.95rem', fontWeight: 600 }}>{currencySymbol} {ticker.ult.toFixed(2)}</span>
                  <span style={{ color: isPositive ? '#4ade80' : '#f87171', fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontWeight: 600, fontSize: '0.8rem' }}>{isPositive ? '▲' : '▼'} {Math.abs(ticker.var).toFixed(2)}%</span>
                </div>
              );
            })}
          </div>
        </div>
      </header>
    </>
  );
}